/* eslint-disable react/no-array-index-key */
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '~/common/providers/toast';
import { SelectionBadge } from '~/common/components/SelectionBadge';
import { IDropdownOption } from '~/common/components/Dropdown/types';
import { api } from '~/common/api';

import { SubcategoriesRow, BadgesList } from './styles';
import { ICategoryListItem } from '../../interfaces/i-category-list-item';
import { PromptTypesEnum } from '../../enums/prompt-types-enum';

type TSubcategoryFilter = {
  id: string;
  options: IDropdownOption[];
};

interface IAdvancedFiltersProps {
  category: PromptTypesEnum | null;
  current: Record<string, string[]>;
  onSelect: (subcategoryId: string, optionValue: string) => void;
  onFiltersLoaded?: (hasFilters: boolean) => void;
}

export const AdvancedFilters: FC<IAdvancedFiltersProps> = ({
  category,
  current,
  onSelect,
  onFiltersLoaded,
}) => {
  const [filters, setFilters] = useState<TSubcategoryFilter[]>([]);
  const filtersCacheRef = useRef<Record<string, TSubcategoryFilter[]>>({});
  const { showToast } = useToast();

  const setFiltersAndNotify = useCallback(
    (nextFilters: TSubcategoryFilter[]) => {
      setFilters(nextFilters);

      const hasFilters = nextFilters.some(
        (filter) => filter.options.length > 0
      );
      onFiltersLoaded?.(hasFilters);
    },
    [onFiltersLoaded]
  );

  const loadFilters = useCallback(
    async (categoryToLoad: PromptTypesEnum) => {
      try {
        const { data: response } = await api.get<{
          categories: ICategoryListItem[];
        }>('/categories', {
          params: { type: categoryToLoad },
        });

        const loadedFilters = response.categories.map((cat) => ({
          id: cat.id,
          options: cat.options,
        }));

        filtersCacheRef.current[categoryToLoad] = loadedFilters;
        setFiltersAndNotify(loadedFilters);
      } catch {
        showToast({
          severity: 'error',
          description:
            'Erro ao carregar filtros avançados. Tente novamente mais tarde.',
        });
        onFiltersLoaded?.(false);
      }
    },
    [setFiltersAndNotify, showToast, onFiltersLoaded]
  );

  useEffect(() => {
    if (!category) {
      setFiltersAndNotify([]);
      return;
    }

    const cachedFilters = filtersCacheRef.current[category];
    if (cachedFilters) {
      setFiltersAndNotify(cachedFilters);
      return;
    }

    loadFilters(category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const hasOptions = filters.some((f) => f.options.length > 0);

  if (!hasOptions) return <SubcategoriesRow />;

  return (
    <SubcategoriesRow>
      <BadgesList>
        {filters.flatMap((filter) =>
          filter.options.map((option) => (
            <SelectionBadge
              key={`${filter.id}-${option.value}`}
              title={option.label}
              onSelect={() => onSelect(filter.id, option.value as string)}
              selected={
                current[filter.id]?.includes(option.value as string) || false
              }
              variant="subcategory"
            />
          ))
        )}
      </BadgesList>
    </SubcategoriesRow>
  );
};
